import { MutableRefObject, useCallback } from "react";
import type { GridApi } from "ag-grid-community";

type Id = number;

export type PendingMap<T> = Record<Id, Partial<T>>;

export interface BaselineMap<T, V = unknown> {
    ref: MutableRefObject<Map<string, V>>;
    value: (row: T) => V;
}

export interface UseGridSaveOptions<T extends { id: number }> {
    rowData: T[];
    setRowData: React.Dispatch<React.SetStateAction<T[]>>;

    pending: PendingMap<T>;
    setPending: React.Dispatch<React.SetStateAction<PendingMap<T>>>;

    resetBaseline: (rows: T[]) => void;

    gridApiRef?: MutableRefObject<GridApi | undefined>;
    revalidate?: () => void;

    mutateRow: (id: number, patch: Partial<T>) => Promise<unknown>;

    baselines?: BaselineMap<T, any>[];

    optimistic?: boolean;

    onAfterLocalCommit?: (mergedRows: T[]) => void;
    onAfterSaveAll?: () => void;
}

export function useGridSave<T extends { id: number }>(
    opts: UseGridSaveOptions<T>
) {
    const {
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef,
        revalidate,
        mutateRow,
        baselines = [],
        optimistic = true,
        onAfterLocalCommit,
        onAfterSaveAll,
    } = opts;

    const handleSave = useCallback(async () => {
        gridApiRef?.current?.stopEditing();

        const entries = Object.entries(pending).filter(
            ([, patch]) =>
                patch && typeof patch === "object" && Object.keys(patch).length > 0
        ) as [string, Partial<T>][];

        if (entries.length === 0) {
            gridApiRef?.current?.refreshCells({ force: true });
            gridApiRef?.current?.refreshClientSideRowModel("filter");
            return;
        }

        for (const [idStr, patch] of entries) {
            await mutateRow(Number(idStr), patch);
        }

        if (optimistic) {
            const merged = rowData.map((r) =>
                pending[r.id] ? ({ ...r, ...pending[r.id] } as T) : r
            );

            for (const b of baselines) {
                const next = new Map<string, any>();
                for (const r of merged) next.set(String(r.id), b.value(r));
                b.ref.current = next;
            }

            setRowData(merged);
            resetBaseline(merged);
            setPending({});

            gridApiRef?.current?.refreshCells({ force: true });
            gridApiRef?.current?.refreshClientSideRowModel("filter");

            onAfterLocalCommit?.(merged);
            revalidate?.();
        } else {
            setPending({});
            revalidate?.();
        }

        onAfterSaveAll?.();
    }, [
        pending,
        rowData,
        mutateRow,
        gridApiRef,
        optimistic,
        setRowData,
        resetBaseline,
        setPending,
        baselines,
        revalidate,
        onAfterLocalCommit,
        onAfterSaveAll,
    ]);

    return { handleSave };
}
